import React, { useState } from "react";
import { Badge } from "../../../ui/badge";

export default function CallRightPanel({ callDetails }) {
  const [activeTab, setActiveTab] = useState("Overview");
  const [selectedTopic, setSelectedTopic] = useState(null);

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSpeakerName = (speaker) => {
    return speaker === "A" ? (callDetails?.speaker_label?.A || "Speaker A") : (callDetails?.speaker_label?.B || "Speaker B");
  };

  const getSpeakerColor = (speaker) => {
    return speaker === "A" ? "text-[#B692F6]" : "text-[#BA24D5]";
  };

  const handleTopicClick = (topic) => {
    setSelectedTopic(selectedTopic?.text === topic.text ? null : topic);
  };

 
  const hasOverviewData = callDetails.overview?.summary || 
                         (callDetails.overview?.next_actions && callDetails.overview.next_actions.length > 0) ||
                         (callDetails.overview?.topics && callDetails.overview.topics.length > 0);

  
  const hasSentimentData = callDetails.sentiment && (
    callDetails.sentiment.overall ||
    (callDetails.sentiment.positives && callDetails.sentiment.positives.length > 0) ||
    (callDetails.sentiment.negatives && callDetails.sentiment.negatives.length > 0)
  );


  const hasSummary = !!callDetails.overview?.summary;
  const hasNextActions = !!(callDetails.overview?.next_actions && callDetails.overview.next_actions.length > 0);
  const hasTopics = !!(callDetails.overview?.topics && callDetails.overview.topics.length > 0);
  
  const hasOverallSentiment = !!callDetails.sentiment?.overall;
  const hasPositives = !!(callDetails.sentiment?.positives?.length > 0);
  const hasNegatives = !!(callDetails.sentiment?.negatives?.length > 0);

  
  if (!hasOverviewData && !hasSentimentData) {
    return null;
  }

 
  const availableTabs = [];
  if (hasOverviewData) availableTabs.push("Overview");
  if (hasSentimentData) availableTabs.push("Sentiment");

 
  const currentActiveTab = availableTabs.includes(activeTab) ? activeTab : availableTabs[0];

  return (
    <div className="space-y-0 border border-[#EAECF0] dark:border-[#1F242F] rounded-lg h-full flex flex-col">
      {/* Tab Headers */}
      {availableTabs.length > 1 && (
        <div className="border-b border-[#EAECF0] dark:border-[#1F242F] bg-white dark:bg-[#0C111D] px-3 pt-3 flex-shrink-0">
          <nav className="-mb-px flex space-x-8">
            {availableTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-0 border-b-2 font-semibold text-sm ${
                  currentActiveTab === tab
                    ? "border-[#6941C6] dark:border-[#CECFD2] text-[#6941C6] dark:text-[#CECFD2] "
                    : "border-transparent text-[#717680] dark:text-[#94979C] hover:text-[#717680] hover:border-gray-300 dark:hover:text-gray-300"
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Tab Content */}
      {currentActiveTab === "Overview" && hasOverviewData && (
        <div className="bg-white dark:bg-[#0C111D] space-y-6 p-4 flex-1 overflow-y-auto">
          {/* Summary Section */}
          {hasSummary && (
            <div className={`${(hasNextActions || hasTopics) ? 'border-b border-[#EAECF0] dark:border-[#1F242F] pb-6' : ''}`}>
              <h3 className="text-sm font-semibold text-[#414651] dark:text-[#CECFD2] mb-2">
                Summary
              </h3>
              <p className="text-sm text-[#475467] dark:text-[#94969C] leading-relaxed">
                {callDetails.overview.summary}
              </p>
            </div>
          )}

          {/* Next Actions Section */}
          {hasNextActions && (
            <div className={`${hasTopics ? 'border-b border-[#EAECF0] dark:border-[#1F242F] pb-6' : ''}`}>
              <h3 className="text-sm font-semibold text-[#414651] dark:text-[#CECFD2] mb-3">
                Next action items
              </h3>
              <div className="space-y-2">
                {callDetails.overview.next_actions.map((action, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <p className="text-sm text-[#475467] dark:text-[#94969C] leading-relaxed">
                      {action.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Topics Section */}
          {hasTopics && (
            <div>
              <h3 className="text-sm font-semibold text-[#414651] dark:text-white mb-3">
                Topics
              </h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {callDetails.overview.topics.map((topic, index) => (
                  <Badge 
                    key={index} 
                    variant="outline" 
                    onClick={() => handleTopicClick(topic)}
                    className={`text-sm px-3 py-4 rounded-md border shadow-sm font-semibold cursor-pointer transition-all ${
                      selectedTopic?.text === topic.text
                        ? "border-[#D6BBFB] bg-[#F9F5FF] text-[#414651]"
                        : "border-[#D5D7DA] bg-[#FFFFFF] text-[#414651] hover:bg-gray-100 dark:border-[#373A41] dark:text-[#CECFD2] dark:bg-[#13161B]"
                    }`}
                  >
                    {topic.text} ({topic.count})
                  </Badge>
                ))}
              </div>

              {/* Topic References */}
              {selectedTopic && (
                <div className="mt-4 p-2">
                  <div className="space-y-3">
                    {selectedTopic.references?.map((reference, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-semibold text-sm ${getSpeakerColor(reference.speaker)}`}>
                            {getSpeakerName(reference.speaker)}
                          </span>
                          <span className="text-xs text-[#475467] dark:text-gray-400">
                            {formatTime(reference.start)}
                          </span>
                        </div>
                        <div className="text-sm text-[#475467] dark:text-[#CECFD2] leading-relaxed">
                          {reference.text || "No transcript text available"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {currentActiveTab === "Sentiment" && hasSentimentData && (
        <div className="bg-white dark:bg-[#0C111D] space-y-3 p-4 flex-1 overflow-y-auto">
          {/* Overall Sentiment */}
          {hasOverallSentiment && (
            <div className={`${(hasPositives || hasNegatives) ? 'border-b border-[#EAECF0] dark:border-[#1F242F] pb-4' : ''}`}>
              <div className="flex items-center space-x-3">
                <h3 className="text-sm font-semibold text-[#414651] dark:text-white">
                  Overall sentiment
                </h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
                  callDetails.sentiment?.overall === "positive" 
                    ? "bg-green-100 text-green-800 border-green-200"
                    : callDetails.sentiment?.overall === "negative"
                    ? "bg-red-100 text-red-800 border-red-200"
                    : "bg-yellow-100 text-yellow-800 border-yellow-200"
                }`}>
                  {callDetails.sentiment.overall || "Neutral"}
                </span>
              </div>
            </div>
          )}

          {/* Positive Highlights */}
          {hasPositives && (
            <div className={`${hasNegatives ? 'border-b border-[#EAECF0] dark:border-[#1F242F] pb-6' : ''}`}>
              <div className="flex items-center space-x-3 mb-4">
                <h3 className="text-sm font-semibold text-[#414651] dark:text-white">
                  Positive highlights
                </h3>
                <span className="w-6 h-6 rounded-full bg-[#FAFAFA] text-[#414651] text-sm font-medium flex items-center justify-center border border-[#E9EAEB]">
                  {callDetails.sentiment.positives.length}
                </span>
              </div>
              
              <div className="space-y-4">
                {callDetails.sentiment.positives.map((positive, index) => (
                  <div key={index}>
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className={`font-semibold text-sm ${getSpeakerColor(positive.speaker)}`}>
                        {getSpeakerName(positive.speaker)}
                      </h4>
                    </div>
                    <p className="text-[#475467] text-sm leading-relaxed">
                      <span className="text-green-600 font-medium">{positive.text}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Negative Highlights */}
          {hasNegatives && (
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <h3 className="text-lg font-semibold text-[#414651] dark:text-white">
                  Negative highlights
                </h3>
                <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-sm font-medium flex items-center justify-center">
                  {callDetails.sentiment.negatives.length}
                </span>
              </div>
              
              <div className="space-y-4">
                {callDetails.sentiment.negatives.map((negative, index) => (
                  <div key={index}>
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className={`font-semibold ${getSpeakerColor(negative.speaker)}`}>
                        {getSpeakerName(negative.speaker)}
                      </h4>
                    </div>
                    <p className="text-[#475467] text-sm leading-relaxed">
                      <span className="text-red-600 font-medium">{negative.text}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
} 